import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { authService } from '../services/authService';

const EmailVerificationCallback: React.FC = () => {
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Extract tokens from URL hash
        const hash = location.hash;
        const urlParams = new URLSearchParams(hash.substring(1));
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        if (!accessToken) {
          throw new Error('No access token found in URL');
        }

        // Store the tokens in localStorage for the auth context
        if (accessToken && refreshToken) {
          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('supabase_refresh_token', refreshToken);
        }

        // Get current user to verify the session
        try {
          const currentUser = await authService.getCurrentUser();
          console.log('Email verification successful, user:', currentUser);
          
          // Update user verification status in the backend
          try {
            const { ApiService } = await import('../services/api');
            const apiService = new ApiService();
            
            // Call the backend to mark user as verified
            await apiService.post('/api/v1/signup-progress/step5', {
              // No additional data needed, just marking as verified
            });
            
            console.log('User verification status updated successfully');
          } catch (updateError) {
            console.warn('Could not update verification status, but email is verified:', updateError);
          }
        } catch (userError) {
          console.warn('Could not fetch current user, but tokens are valid:', userError);
        }
        
        setStatus('success');
        
        // Redirect to signup step 2 after a short delay
        setTimeout(() => {
          navigate('/signup?step=2&verified=true');
        }, 2000);
        
      } catch (error: any) {
        console.error('Email verification error:', error);
        setError(error.message || 'Failed to verify email');
        setStatus('error');
      }
    };

    handleEmailVerification();
  }, [location, navigate]);

  const handleRetry = () => {
    setStatus('verifying');
    setError('');
    // Retry the verification
    window.location.reload();
  };

  const handleContinue = () => {
    navigate('/signup?step=2&verified=true');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
        <div className="mb-6">
          {status === 'verifying' && (
            <ArrowPathIcon className="w-16 h-16 text-purple-600 mx-auto mb-4 animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
          )}
          {status === 'error' && (
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          )}
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {status === 'verifying' && 'Verifying your email...'}
          {status === 'success' && 'Email verified successfully!'}
          {status === 'error' && 'Verification failed'}
        </h1>

        <p className="text-gray-600 mb-6">
          {status === 'verifying' && 'Please wait while we verify your email address.'}
          {status === 'success' && 'Your email has been verified. Redirecting you to continue your signup...'}
          {status === 'error' && error}
        </p>

        {status === 'success' && (
          <button
            onClick={handleContinue}
            className="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors"
          >
            Continue Signup
          </button>
        )}

        {status === 'error' && (
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-200 transition-colors"
            >
              Back to Signup
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationCallback;
